<template>
    <div class="relative bg-white ring-1 ring-black/10 rounded-lg shadow-sm overflow-hidden">
        <div class="bg-gray-50 border-b border-gray-200 px-6 py-4">
            <div class="flex items-center justify-between">
                <div class="flex-1 flex items-center space-x-4">
                    <div class="space-y-1.5">
                        <h3 class="text-lg leading-none"><span class="font-mono">#{{ report.number.toString().padStart(8, '0') }}</span></h3>
                        <div class="flex items-center space-x-2 text-sm text-gray-600">
                            <span>{{ dayjs(report.updated_at).format('MMM D, YYYY HH:mm') }}</span>
                            <span class="text-gray-300">·</span>
                            <span>{{ report.user.name }}</span>
                            <BadgeUserRole :role="report.user.role" minimal />
                        </div>
                    </div>
                    <UBadge v-if="!isRegularReport" color="orange" label="Feedback" variant="solid" />
                </div>
                <div class="flex-shrink-0">
                    <UDropdown :items="actionItems" :popper="{ placement: 'bottom-end' }" :ui="{ width: 'w-56' }">
                        <UButton
                            label="Actions"
                            trailing-icon="i-ph-caret-down"
                            color="gray"
                        />
                    </UDropdown>
                </div>
            </div>
        </div>
        <div class="grid grid-cols-6 gap-5 px-6 py-5">
            <template v-if="report.call">
                <Field label="Call ID" class="col-span-2">
                    <span class="font-mono">{{ report.call.number.toString().padStart(8, '0') }}</span>
                </Field>
                <Field label="Call Source" class="col-span-2">
                    <BadgeCallSource :source="report.call.source" />
                </Field>
                <Field label="Call Status" class="col-span-2">
                    <BadgeCallStatus :status="report.call.status" />
                </Field>
            </template>
            <template v-if="isRegularReport">
                <div class="col-span-2">
                    <p class="text-xs uppercase leading-6 font-medium text-gray-500">Reason for Calling</p>
                    <p v-if="report.call_reason" class="text-gray-800">{{ report.call_reason.label }} / {{ report.call_reason.description }}</p>
                    <p v-else class="text-gray-400">--</p>
                </div>
                <div class="col-span-2">
                    <p class="text-xs uppercase leading-6 font-medium text-gray-500">Suspected Diagnosis</p>
                    <div v-if="report.diagnosis" class="flex items-center space-x-2 text-gray-800">
                        <p>{{ report.diagnosis.label }} / {{ report.diagnosis.description }}</p>
                        <BadgePriority v-if="report.diagnosis.metadata?.priority" :priority="report.diagnosis.metadata.priority" />
                    </div>
                    <p v-else class="text-gray-400">--</p>
                </div>
                <div class="col-span-2">
                    <p class="text-xs uppercase leading-6 font-medium text-gray-500">Final Decision</p>
                    <p v-if="report.call_decision" class="text-gray-800">{{ report.call_decision.label }} / {{ report.call_decision.description }}</p>
                    <p v-else class="text-gray-400">--</p>
                </div>
            </template>
            <div v-if="documents.length" class="col-span-6">
                <p class="text-xs uppercase leading-6 font-medium text-gray-500">Documents</p>
                <ul class="mt-1 space-y-1">
                    <li v-for="doc in documents" :key="doc.id">
                        <button
                            type="button"
                            class="group inline-flex items-center gap-2 text-sm text-gray-800 hover:text-brand-500 disabled:opacity-60"
                            :disabled="viewingId === doc.id"
                            @click="viewDocument(doc)"
                        >
                            <UIcon :name="documentIcon(doc.mime_type)" class="size-4 text-gray-500 shrink-0" />
                            <span class="underline-offset-2 group-hover:underline">{{ doc.file_name }}</span>
                            <UIcon v-if="viewingId === doc.id" name="i-ph-spinner" class="size-3.5 animate-spin text-gray-400" />
                        </button>
                    </li>
                </ul>
            </div>
            <div v-if="isRegularReport && (report.user.role === UserRole.medicalDirector || report.user.role === UserRole.doctor)" class="col-span-6">
                <p class="text-xs uppercase leading-6 font-medium text-gray-500">Recommendation</p>
                <p class="text-gray-800 whitespace-pre-line">
                    {{ report.consultation || '--' }}
                </p>
            </div>
            <div class="col-span-6">
                <p class="text-xs uppercase leading-6 font-medium text-gray-500">Notes</p>
                <p class="text-gray-800 whitespace-pre-line">
                    {{ report.notes || '--' }}
                </p>
            </div>
            <div v-if="isEditing" class="col-span-6">
                <div class="relative">
                    <UTextarea v-model="notes" autoresize :placeholder="editingConsultation ? 'Add recommendation...' : 'Add notes...'" />
                    <div class="absolute bottom-2 right-2 flex items-center space-x-1">
                        <UButton
                            @click="clearEditingNotes()"
                            size="xs"
                            color="gray"
                            label="Cancel"
                        />
                        <UButton
                            @click="onSaveNotes()"
                            :loading="saving"
                            :disabled="!notes"
                            size="xs"
                            color="black"
                            label="Save"
                        />
                    </div>
                </div>
            </div>
        </div>

        <OverlayCallSendConsultationModal 
            v-model:open="overlays.sendConsultation" 
            @reload="emit('refresh')" 
            :report="report"
            :recipient="recipient"
        />

        <OverlayCallSendReportModal
            v-model:open="overlays.sendReport"
            @reload="emit('refresh')"
            :report="report"
            :subscription="subscription"
            :policy-holder="policyHolder"
        />

        <OverlayCallReportDocumentsModal
            v-model:open="documentsOpen"
            :report="report"
            @changed="onDocumentsChanged"
        />
    </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import { FEEDBACK_REPORT_REASON_ID } from '~/types/call'
import { UserRole } from '~/types/user'
import type { CallReport } from '~/types/callReport'
import type { CallReportDocument } from '~/types/callReportDocument'
import type { Subscription } from '~/types/subscription'
import type { Customer } from '~/types/customer'
import { resolveReportRecipient } from '~/utils/reportRecipient'
import { useReportDocuments } from '~/composables/useReportDocuments'

const emit = defineEmits<{
    'refresh': [void]
}>()

const props = defineProps<{
    report: CallReport
    subscription?: Subscription
    caller?: Customer
}>()

const auth = useAuthStore()
const toast = useToast()
const client = useSupabaseClient()
const { listDocuments, getSignedUrl } = useReportDocuments()

const documentsOpen = ref(false)
const documents = ref<CallReportDocument[]>(props.report.documents ?? [])
const viewingId = ref<string | null>(null)

const editingNotes = ref(false)
const editingConsultation = ref(false)
const saving = ref(false)
const notes = ref('')

const overlays = reactive({
    sendReport: false,
    sendConsultation: false,
})

const isReportUser = computed(() => auth.user?.id === props.report.user?.id)
const isRegularReport = computed(() => props.report?.call_reason?.id !== FEEDBACK_REPORT_REASON_ID)
const isEditing = computed(() => editingNotes.value || editingConsultation.value)
const canSendEmailOrConsultation = computed(() => (isReportUser.value && auth.hasRole(UserRole.doctor)) || auth.hasRole(UserRole.medicalDirector))
const policyHolder = computed(() => props.report?.customer?.policy_holder)
const recipient = computed(() => resolveReportRecipient(props.report?.customer, props.caller))
const sendConsultationDisabled = computed(() => !recipient.value || !props.report?.consultation)
const sendReportDisabled = computed(() =>
    props.subscription &&
    props.subscription.partner &&
    !props.subscription.partner.metadata?.email_reports?.call_report?.email_to &&
    !props.subscription.partner.metadata?.email_reports?.sick_leave?.mode
)

const actionItems = computed(() => {
    const sections: any[][] = []

    if (isRegularReport.value) {
        const editActions = []
        if (isReportUser.value) {
            editActions.push({ label: 'Update notes', icon: 'i-ph-pencil-line-bold', click: () => onEditNotes() })
        }
        if (canSendEmailOrConsultation.value) {
            editActions.push({ label: 'Update recommendation', icon: 'i-ph-pencil-line-bold', click: () => onEditConsultation() })
        }
        if (editActions.length) sections.push(editActions)

        if (canSendEmailOrConsultation.value) {
            sections.push([
                { label: 'Send recommendation', icon: 'i-ph-chat-circle-text-bold', disabled: sendConsultationDisabled.value, click: () => { overlays.sendConsultation = true } },
                { label: 'Send email', icon: 'i-ph-paper-plane-tilt-bold', disabled: sendReportDisabled.value, click: () => { overlays.sendReport = true } },
            ])
        }
    }

    sections.push([
        { label: 'Attach Documents', icon: 'i-ph-paperclip', click: () => { documentsOpen.value = true } },
    ])

    return sections
})

function documentIcon(mime: string) {
    return mime === 'application/pdf' ? 'i-ph-file-pdf-duotone' : 'i-ph-file-image-duotone'
}

async function viewDocument(doc: CallReportDocument) {
    viewingId.value = doc.id
    try {
        const url = await getSignedUrl(doc.storage_path)
        window.open(url, '_blank', 'noopener')
    } catch (error: any) {
        toast.add({ title: 'Could not open document', description: error.message, color: 'red' })
    } finally {
        viewingId.value = null
    }
}

async function onDocumentsChanged() {
    try {
        documents.value = await listDocuments(props.report.id)
    } catch (error: any) {
        toast.add({ title: 'Could not refresh documents', description: error.message, color: 'red' })
    }
}

function onEditNotes() {
    editingNotes.value = true
    editingConsultation.value = false
    notes.value = ''
}

function onEditConsultation() {
    editingNotes.value = false
    editingConsultation.value = true
    notes.value = props.report.consultation || ''
}

async function onSaveNotes() {
    if (props.report?.id) {
        saving.value = true

        const updateData = editingNotes.value
            ? { notes: `${props.report.notes}\n\n---- Update: ${dayjs().format('MMM D, YYYY HH:mm')} ----\n${notes.value}` }
            : { consultation: notes.value }

        const { data, error } = await client.from('call_reports')
            .update(updateData as never)
            .eq('id', props.report.id)
            .select('notes, consultation')
            .single()

        if (data) {
            props.report.notes = data.notes
            props.report.consultation = data.consultation
        }
        else if (error) {
            console.log(error)
        }

        saving.value = false
        clearEditingNotes()
    }
}

function clearEditingNotes() {
    editingNotes.value = false
    editingConsultation.value = false
    notes.value = ''
}
</script>
